<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->
<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-view-column-outline" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [formControl]="filterControl" placeholder="Search" />
    @if (filterControl.value) {
    <button mat-icon-button matSuffix (click)="filterControl.setValue(''); $event.stopPropagation()" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

@if (!!galleryToDo()) {
<div class="gallery-warning">** Gallery Parameters not assigned: {{ galleryToDo() }}</div>
}

<div class="frame">
  <div class="table-container">
    <mat-table #table [dataSource]="dataFieldDataSource" matSort (matSortChange)="sortChanged($event)" cdkDropList
      [cdkDropListDisabled]="!allowDragAndDrop" (cdkDropListDropped)="dropHandler($event)">
        <!-- Drag Handler column -->
        <ng-container matColumnDef="draghandle">
          <mat-header-cell *matHeaderCellDef class="column-draghandle"></mat-header-cell>
          <mat-cell *matCellDef="let element;" class="column-draghandle">
            <div class="draghandle-div">
              @if (allowDragAndDrop) {
              <mat-icon cdkDragHandle cdkDragRootElement="row" cdkDrag [cdkDragData]="element" cdkDragLockAxis="y"
                [cdkDragPreviewClass]="userTheme" class="mdi-18px down-12" fontIcon="mdi-drag-vertical" />
              }
            </div>
          </mat-cell>
        </ng-container>
        <ng-container matColumnDef="draghandleSpacer">
          <mat-header-cell *matHeaderCellDef class="column-draghandle"></mat-header-cell>
          <mat-cell *matCellDef="let element;" class="column-draghandle">
            <div class="draghandle-div"></div>
          </mat-cell>
        </ng-container>
        <!-- action column -->
        <ng-container matColumnDef="action">
          <mat-header-cell *matHeaderCellDef class="column-action">
            @if (showTemplates && canEdit()) {
            <span>
              <button mat-icon-button (click)="addOrEditDataField(null, true)" title="Add a template data Field"
                style="outline: none;">
                <mat-icon class="mdi-24px center-self little-left" fontIcon="mdi-plus-circle"></mat-icon>
              </button>
            </span>
            } &nbsp;
            @if (!showTemplates && (canEdit())) {
            <span>
              <button mat-icon-button [matMenuTriggerFor]="addMenu" style="outline: none;"
                title="Add data Field">
                <mat-icon class="mdi-24px self-center" fontIcon="mdi-plus-circle"></mat-icon>
              </button>
            </span>
            } &nbsp;
            <mat-menu #addMenu="matMenu" [overlapTrigger]="false">
              <button mat-menu-item (click)="addOrEditDataField(null, false)" title="Add a new data Field">New Data
                Field</button>
              @for (template of templateDataSource.data; track template.id) {
              <button mat-menu-item (click)="addOrEditDataField(template, false)"
                title="{{ template.name }} - {{ template.description }}">{{ template.name }}</button>
              }
            </mat-menu>
          </mat-header-cell>
          <mat-cell *matCellDef="let element" class="column-action">
            <div class="action-div">
              @if (element.displayOrder > 0) {
              <span>
                @if (canEdit()) {
                <button mat-icon-button class="msel-action"
                  (click)="addOrEditDataField(element, showTemplates); $event.stopPropagation();"
                  title="Edit {{ element.name }}">
                  <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"
                    title="Edit {{ element.name }}"></mat-icon>
                </button>
                }
                @if (canEdit()) {
                <button mat-icon-button class="msel-action"
                  (click)="deleteDataField(element); $event.stopPropagation();" title="Delete {{ element.name }}">
                  <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"
                    title="Delete {{ element.name }}"></mat-icon>
                </button>
                }
                @if (!injectTypeId && !showTemplates && canEdit()) {
                <button mat-icon-button class="msel-action"
                  (click)="addOrEditDataField(element, true); $event.stopPropagation();"
                  title="Make {{ element.name }} a template">
                  <mat-icon class="mdi-18px" fontIcon="mdi-bank" title="Make {{ element.name }} a template"></mat-icon>
                </button>
                }
              </span>
              }
              @if (element.displayOrder < 0) { <span style="margin-left: 10px; font-size: 12px;">*System Defined*</span>
                }
                &nbsp;
            </div>
            <div>{{ !showTemplates && element.displayOrder > 0 ? element.displayOrder : ' '}}</div>
          </mat-cell>
        </ng-container>
        <!-- events Column -->
        <ng-container matColumnDef="events">
          <mat-header-cell mat-sort-header="onScenarioEventList" title="Display on Scenario Events list?"
            *matHeaderCellDef class="column-display">
            &nbsp;&nbsp;&nbsp;&nbsp;List
          </mat-header-cell>
          <mat-cell *matCellDef="let element" class="column-display">
            <mat-checkbox [(ngModel)]="element.onScenarioEventList" [disabled]="!canEdit()"
              (change)="saveChange(element)" (click)="$event.stopPropagation()"
              title="Display on Scenario Events list?">
            </mat-checkbox>
          </mat-cell>
        </ng-container>
        <!-- exercise Column -->
        <ng-container matColumnDef="exercise">
          <mat-header-cell mat-sort-header="onExerciseView" title="Display on the Exercise View?" *matHeaderCellDef
            class="column-display">
            &nbsp;&nbsp;&nbsp;&nbsp;View
          </mat-header-cell>
          <mat-cell *matCellDef="let element" class="column-display">
            <mat-checkbox [(ngModel)]="element.onExerciseView" [disabled]="!canEdit()" (change)="saveChange(element)"
              (click)="$event.stopPropagation();" title="Display on the Exercise View?">
            </mat-checkbox>
          </mat-cell>
        </ng-container>
        <!-- information Column -->
        <ng-container matColumnDef="information">
          <mat-header-cell mat-sort-header="isInformationField" title="Display for Information Events?"
            *matHeaderCellDef class="column-display">
            &nbsp;&nbsp;&nbsp;&nbsp;Info
          </mat-header-cell>
          <mat-cell *matCellDef="let element" class="column-display">
            @if (element.displayOrder >= 0) {
            <mat-checkbox [(ngModel)]="element.isInformationField" [disabled]="!canEdit()"
              (change)="saveChange(element)" (click)="$event.stopPropagation();"
              title="Display for Information Events?">
            </mat-checkbox>
            }
          </mat-cell>
        </ng-container>
        <!-- facilitation Column -->
        <ng-container matColumnDef="facilitation">
          <mat-header-cell mat-sort-header="isFacilitationField" title="Display for Facilitation Events?"
            *matHeaderCellDef class="column-display">
            &nbsp;&nbsp;&nbsp;&nbsp;Facil
          </mat-header-cell>
          <mat-cell *matCellDef="let element" class="column-display">
            @if (element.displayOrder >= 0) {
            <mat-checkbox [(ngModel)]="element.isFacilitationField" [disabled]="!canEdit()"
              (change)="saveChange(element)" (click)="$event.stopPropagation();"
              title="Display for Facilitation Events?">
            </mat-checkbox>
            }
          </mat-cell>
        </ng-container>
        <!-- name Column -->
        <ng-container matColumnDef="name">
          <mat-header-cell mat-sort-header="name" *matHeaderCellDef class="column-name">
            Name
          </mat-header-cell>
          <mat-cell *matCellDef="let element" class="column-name">
            {{ element.name }}
          </mat-cell>
        </ng-container>
        <!-- datatype Column -->
        <ng-container matColumnDef="datatype">
          <mat-header-cell mat-sort-header="dataType" *matHeaderCellDef class="six-cell"> Data Type </mat-header-cell>
          <mat-cell *matCellDef="let element" class="six-cell">
            <div class="summary-text">{{ element.dataType }}</div>
          </mat-cell>
        </ng-container>
        <!-- options Column -->
        <ng-container matColumnDef="options">
          <mat-header-cell *matHeaderCellDef class="six-cell"> Options </mat-header-cell>
          <mat-cell *matCellDef="let element" class="six-cell">
            <div class="summary-text">{{ getDataOptionsString(element) }}</div>
          </mat-cell>
        </ng-container>
        <!-- integration Column -->
        <ng-container matColumnDef="integration">
          <mat-header-cell *matHeaderCellDef class="six-cell"> Integration </mat-header-cell>
          <mat-cell *matCellDef="let element" class="six-cell">
            @if (element.displayOrder > 0) {
            <mat-select class="integration" [(ngModel)]="element.galleryArticleParameter"
              (selectionChange)="saveChange(element)" (click)="$event.stopPropagation();" [disabled]="!canEdit()"
              [compareWith]="compareIntegrationValues">
              <mat-option value="">- - -</mat-option>
              @for (option of getUnusedGalleryOptions(element.galleryArticleParameter); track option) {
              <mat-option value="{{ option }}">Gallery {{ option }}</mat-option>
              }
            </mat-select>
            }
            @if (element.displayOrder < 0) { <span>{{ element.galleryArticleParameter }}</span>
              }
          </mat-cell>
        </ng-container>
        <!-- row definitions -->
        <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
        <mat-row *matRowDef="let row; columns: systemFieldDisplayedColumns when: fieldIsSystemDefined"></mat-row>
        <mat-row *matRowDef="let row; columns: displayedColumns; when: !fieldIsSystemDefined"></mat-row>
    </mat-table>
    <!-- No Results Message -->
    @if (dataFieldDataSource?.filteredData.length === 0) {
    <div class="text no-results">No results found</div>
    }
  </div>
</div>
